import React, { useCallback, useMemo, useState } from "react";
import { message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTokens } from "../../../core/theme/TokensProvider";
import { Button } from "../../../ui/primitives/Button";
import { Drawer } from "../../../ui/primitives/Drawer";
import { Input } from "../../../ui/primitives/Input";
import { Select } from "../../../ui/primitives/Select";
import { Stack } from "../../../ui/primitives/Stack";
import { Switch } from "../../../ui/primitives/Switch";
import { Tag } from "../../../ui/primitives/Tag";
import { Text } from "../../../ui/primitives/Text";
import { useRotaService } from "../../../services/members/RotaServiceProvider";
import { RosterStaffMember, RotaSlot, StaffRole } from "../../../model/members/Rota";
import { ROTA_DAY_NAMES, formatShiftWindow, shiftTypeFor, slotsForDay } from "../../../utils/rotaUtils";

interface ManageStaffDrawerProps {
  open: boolean;
  onClose: () => void;
  slots: RotaSlot[];
  /** Active Staff-type members — the pool that can be put on a shift. */
  receptionStaff: RosterStaffMember[];
  /**
   * The whole roster. The drawer narrows it to Staff itself (Management are
   * deliberately absent from this screen) but needs the deactivated ones too, so removing
   * someone is reversible.
   */
  roster: RosterStaffMember[];
  onChanged: () => void;
}

const UNASSIGNED = "unassigned";

/** In-progress edits to one roster row, before they are saved. */
interface StaffDraft {
  name: string;
  email: string;
}

const DAY_OPTIONS = ROTA_DAY_NAMES.map((name, index) => ({ label: name, value: index }));

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Manage Staff — the one place the rota is edited. Reassigning who covers a shift, adding
 * a new reception team member and removing one all live here, so the Work Allocation page
 * itself stays read-only about *who* is working and is only about *what* they're doing.
 */
const ManageStaffDrawer: React.FC<ManageStaffDrawerProps> = ({
  open,
  onClose,
  slots,
  receptionStaff,
  roster,
  onChanged,
}) => {
  const service = useRotaService();
  const tokens = useTokens();

  const [newName, setNewName] = useState<string>("");
  const [newEmail, setNewEmail] = useState<string>("");
  const [busy, setBusy] = useState<boolean>(false);

  // Per-row edits to the existing team, keyed by staff id.
  const [drafts, setDrafts] = useState<Record<number, StaffDraft>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  // Add-shift form.
  const [newDay, setNewDay] = useState<number>(0);
  const [newStart, setNewStart] = useState<string>("");
  const [newEnd, setNewEnd] = useState<string>("");
  const [newStaffId, setNewStaffId] = useState<number | null>(null);
  // Defaults to Staff — the common case is adding someone who works shifts. Management is
  // for club owners/admins, who allocate work rather than receive it (FORT-352).
  const [newRole, setNewRole] = useState<StaffRole>("Staff");
  const [savingShift, setSavingShift] = useState<boolean>(false);

  // Deactivated staff DO appear, so removing someone can be undone.
  const shiftRoster = useMemo(() => roster.filter((s) => s.role === "Staff"), [roster]);

  // Management are listed separately rather than hidden: before FORT-352 they were invisible
  // here, so there was no way to see or add a club owner/admin — which is exactly what the
  // Management type is for. They stay out of shiftRoster because only Staff go on shifts.
  const managementRoster = useMemo(() => roster.filter((s) => s.role === "Management"), [roster]);

  // "Unassigned" is a real choice, so it needs a sentinel value — an empty-string option
  // would be indistinguishable from allowClear's undefined.
  const staffOptions = useMemo(
    () => [
      { label: "Unassigned", value: UNASSIGNED },
      ...receptionStaff.map((s) => ({ label: s.name, value: s.id })),
    ],
    [receptionStaff]
  );

  const reassign = useCallback(
    async (slot: RotaSlot, value: string | number | undefined) => {
      const staffId = value === UNASSIGNED || value == null ? null : Number(value);
      try {
        await service.updateSlot(slot.id, {
          dayOfWeek: slot.dayOfWeek,
          startTime: slot.startTime,
          endTime: slot.endTime,
          staffId,
        });
        onChanged();
      } catch {
        // The API client already surfaces the failure as a notification.
      }
    },
    [service, onChanged]
  );

  const addSlot = useCallback(async () => {
    const start = newStart.trim();
    const end = newEnd.trim();
    if (!HHMM.test(start) || !HHMM.test(end)) {
      message.error("Enter both times as HH:mm, e.g. 05:00.");
      return;
    }
    if (start === end) {
      message.error("A shift's start and end times must differ.");
      return;
    }
    setSavingShift(true);
    try {
      await service.createSlot({ dayOfWeek: newDay, startTime: start, endTime: end, staffId: newStaffId });
      setNewStart("");
      setNewEnd("");
      setNewStaffId(null);
      onChanged();
    } catch {
      // The API client already surfaces the failure as a notification.
    } finally {
      setSavingShift(false);
    }
  }, [service, newDay, newStart, newEnd, newStaffId, onChanged]);

  const removeSlot = useCallback(
    async (slot: RotaSlot) => {
      try {
        await service.deleteSlot(slot.id);
        onChanged();
      } catch {
        // The API client already surfaces the failure as a notification.
      }
    },
    [service, onChanged]
  );

  const addStaff = useCallback(async () => {
    const name = newName.trim();
    if (!name) {
      message.error("A staff name is required.");
      return;
    }
    setBusy(true);
    try {
      await service.createStaff({
        name,
        email: newEmail.trim() || null,
        role: newRole,
      });
      setNewName("");
      setNewEmail("");
      setNewRole("Staff");
      onChanged();
    } catch {
      // The API client already surfaces the failure as a notification.
    } finally {
      setBusy(false);
    }
  }, [service, newName, newEmail, newRole, onChanged]);

  // Removing someone deactivates rather than deletes them: their historical call and
  // follow-up attribution has to survive, and they can be brought back with the same
  // switch. The API clears a deactivated (or promoted) member off the rota, leaving the
  // shifts themselves in place as "Unassigned" — they still need covering.
  // Resolves true when the change stuck. Callers that hold unsaved input need to know:
  // swallowing the failure here and reporting success would throw away what the user typed.
  const saveStaff = useCallback(
    async (member: RosterStaffMember, patch: Partial<RosterStaffMember>): Promise<boolean> => {
      const next = { ...member, ...patch };
      try {
        await service.updateStaff(member.id, {
          name: next.name,
          email: next.email,
          isActive: next.isActive,
          role: next.role,
          defaultShiftDefinitionId: next.defaultShiftDefinitionId,
        });
        onChanged();
        return true;
      } catch {
        // The API client already surfaces the failure as a notification.
        return false;
      }
    },
    [service, onChanged]
  );

  // Name and email are edited in place, so each row needs somewhere to hold keystrokes
  // until they're saved. A row with no entry here is showing exactly what the server has.
  const draftFor = useCallback(
    (member: RosterStaffMember): StaffDraft =>
      drafts[member.id] ?? { name: member.name, email: member.email ?? "" },
    [drafts]
  );

  const editRow = useCallback((member: RosterStaffMember, patch: Partial<StaffDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [member.id]: { ...(prev[member.id] ?? { name: member.name, email: member.email ?? "" }), ...patch },
    }));
  }, []);

  const saveRow = useCallback(
    async (member: RosterStaffMember) => {
      const draft = draftFor(member);
      const name = draft.name.trim();
      if (!name) {
        message.error("A staff name is required.");
        return;
      }
      setSavingId(member.id);
      try {
        // Blank means "no email" — send null so the API unlinks the login rather than
        // storing an empty string that no token could ever match.
        const saved = await saveStaff(member, { name, email: draft.email.trim() || null });
        // Only on success: a rejected save (a duplicate email, most likely) has to leave
        // the address on screen so it can be corrected, not silently revert it.
        if (saved) {
          setDrafts((prev) => {
            const { [member.id]: _discarded, ...rest } = prev;
            return rest;
          });
        }
      } finally {
        setSavingId(null);
      }
    },
    [draftFor, saveStaff]
  );

  return (
    <Drawer title="Manage staff" open={open} onClose={onClose} width={480} data-testid="manage-staff-drawer">
      <Stack gap={6}>
        <Stack gap={3}>
          <Text variant="h5">Staff ({receptionStaff.length} active)</Text>
          <Text color="secondary">
            Switching someone off removes them from the rota; their shifts stay, unassigned.
            Switch them back on to return them to the team. Adding someone's email links
            their login, so their own work follows them to any machine.
          </Text>
          {shiftRoster.length === 0 ? (
            <Text color="secondary">No staff yet — add your team below.</Text>
          ) : (
            shiftRoster.map((member) => {
              const draft = draftFor(member);
              const dirty = draft.name !== member.name || draft.email !== (member.email ?? "");
              return (
                <Stack
                  key={member.id}
                  gap={2}
                  style={{
                    border: `1px solid ${tokens.color.border.default}`,
                    borderRadius: tokens.radius.md,
                    padding: tokens.space[3],
                  }}
                  data-testid={`roster-row-${member.id}`}
                >
                  <Stack direction="horizontal" justify="space-between" align="center" gap={2} wrap>
                    <Input
                      style={{ flex: 1, minWidth: 140 }}
                      value={draft.name}
                      placeholder="Name"
                      data-testid={`roster-name-${member.id}`}
                      onChange={(e) => editRow(member, { name: e.target.value })}
                    />
                    <Switch
                      checked={member.isActive}
                      data-testid={`roster-active-${member.id}`}
                      onChange={(checked) => saveStaff(member, { isActive: checked })}
                    />
                  </Stack>
                  <Input
                    type="email"
                    value={draft.email}
                    placeholder="Email (optional — links their login)"
                    data-testid={`roster-email-${member.id}`}
                    onChange={(e) => editRow(member, { email: e.target.value })}
                  />
                  <Stack direction="horizontal" justify="space-between" align="center" gap={2} wrap>
                    <Text variant="caption" color="secondary">
                      {member.email ? "Linked to a login" : "Signs in with the staff picker"}
                    </Text>
                    <Button
                      variant="primary"
                      size="sm"
                      loading={savingId === member.id}
                      disabled={!dirty || !draft.name.trim()}
                      data-testid={`roster-save-${member.id}`}
                      onClick={() => saveRow(member)}
                    >
                      Save
                    </Button>
                  </Stack>
                </Stack>
              );
            })
          )}
        </Stack>

        <Stack gap={3}>
          <Text variant="h5">Management ({managementRoster.length})</Text>
          <Text color="secondary">
            Club owners and admins. They allocate work rather than receive it, so they never
            appear on the rota. Adding their email links their login, so the "who's on the
            desk" picker resolves them automatically instead of asking.
          </Text>
          {managementRoster.length === 0 ? (
            <Text color="secondary">No management staff yet.</Text>
          ) : (
            managementRoster.map((member) => (
              <Stack
                key={member.id}
                direction="horizontal"
                gap={2}
                align="center"
                data-testid={`roster-management-${member.id}`}
                style={{
                  border: `1px solid ${tokens.color.border.default}`,
                  borderRadius: tokens.radius.md,
                  padding: tokens.space[3],
                  justifyContent: "space-between",
                }}
              >
                <Stack gap={0}>
                  <Text>{member.name}</Text>
                  <Text color="secondary">{member.email || "No email linked"}</Text>
                </Stack>
                <Tag tone={member.isActive ? "success" : "default"}>
                  {member.isActive ? "Active" : "Inactive"}
                </Tag>
              </Stack>
            ))
          )}
        </Stack>

        <Stack gap={2}>
          <Text variant="h5">Add a staff member</Text>
          <Input
            placeholder="Name"
            value={newName}
            data-testid="roster-add-name"
            onChange={(e) => setNewName(e.target.value)}
          />
          <Input
            type="email"
            placeholder="Email (optional — links their login)"
            value={newEmail}
            data-testid="roster-add-email"
            onChange={(e) => setNewEmail(e.target.value)}
          />
          <Select
            value={newRole}
            onChange={(value) => setNewRole(value as StaffRole)}
            options={[
              { label: "Staff — works shifts and receives allocated work", value: "Staff" },
              { label: "Management — allocates work, not on the rota", value: "Management" },
            ]}
            data-testid="roster-add-role"
          />
          <Button
            variant="primary"
            icon={<PlusOutlined />}
            loading={busy}
            disabled={!newName.trim()}
            onClick={addStaff}
            data-testid="roster-add-btn"
          >
            {newRole === "Management" ? "Add to management" : "Add to the team"}
          </Button>
        </Stack>

        <Stack gap={3}>
          <Text variant="h5">Shift assignments</Text>
          <Text color="secondary">
            Who covers each shift. The rota repeats every week until you change it here.
          </Text>
          {slots.length === 0 ? (
            <Text color="secondary">No shifts yet — add the club's weekly shifts below.</Text>
          ) : (
            ROTA_DAY_NAMES.map((dayName, dayOfWeek) => {
              const daySlots = slotsForDay(slots, dayOfWeek);
              if (daySlots.length === 0) return null;
              return (
                <Stack key={dayName} gap={2} data-testid={`roster-day-${dayOfWeek}`}>
                  <Text variant="label" color="secondary">
                    {dayName}
                  </Text>
                  {daySlots.map((slot) => (
                    <Stack key={slot.id} direction="horizontal" gap={2} align="center" wrap>
                      <Tag tone="default">{shiftTypeFor(slot)}</Tag>
                      <Text
                        color="secondary"
                        style={{ minWidth: 110, fontSize: tokens.fontSize.xs, whiteSpace: "nowrap" }}
                      >
                        {formatShiftWindow(slot)}
                      </Text>
                      <Select
                        style={{ flex: 1, minWidth: 140 }}
                        options={staffOptions}
                        value={slot.staffId ?? UNASSIGNED}
                        onChange={(value) => reassign(slot, value as string | number | undefined)}
                        data-testid={`roster-slot-staff-${slot.id}`}
                      />
                      <Button
                        variant="link"
                        size="sm"
                        data-testid={`roster-slot-remove-${slot.id}`}
                        onClick={() => removeSlot(slot)}
                      >
                        Remove
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              );
            })
          )}
        </Stack>

        <Stack gap={2}>
          <Text variant="h5">Add a shift</Text>
          <Text color="secondary">This shift repeats every week until it's changed or removed.</Text>
          <Select
            label="Day"
            options={DAY_OPTIONS}
            value={newDay}
            onChange={(value) => setNewDay(Number(value))}
            data-testid="add-shift-day"
          />
          <Input
            label="Start time (HH:mm)"
            placeholder="05:00"
            value={newStart}
            data-testid="add-shift-start"
            onChange={(e) => setNewStart(e.target.value)}
          />
          <Input
            label="End time (HH:mm)"
            placeholder="13:00"
            value={newEnd}
            data-testid="add-shift-end"
            onChange={(e) => setNewEnd(e.target.value)}
          />
          <Select
            label="Who's covering it"
            options={staffOptions}
            value={newStaffId ?? UNASSIGNED}
            onChange={(value) => setNewStaffId(value === UNASSIGNED || value == null ? null : Number(value))}
            data-testid="add-shift-staff"
          />
          <Button
            variant="primary"
            icon={<PlusOutlined />}
            loading={savingShift}
            onClick={addSlot}
            data-testid="add-shift-btn"
          >
            Add shift
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default ManageStaffDrawer;
